<template>
	<header class="mall-header" :class="{ 'has-dropdown': activeDropdown }">
		<div class="header-inner">
			<!-- Logo -->
			<router-link to="/mall/home" class="logo">
				<el-image style="width: 147px; height: 40px" :src="headerLogo" />
			</router-link>

			<!-- 导航菜单 -->
			<nav class="header-nav">
				<div v-for="item in navItems" :key="item.id" class="nav-item-wrapper" @mouseenter="onNavEnter(item)" @mouseleave="onNavLeave(item)" @click="gotoNav(item)">
					<span
						class="nav-item"
						:class="{
							'nav-active': isActive(item),
							'has-child': item.child?.length
						}"
					>
						<span class="nav-name">{{ item.langKey ? $t(item.langKey) : item.name?.value }}</span>
						<el-icon v-if="item.child?.length" class="ml-4px nav-arrow">
							<ArrowDown />
						</el-icon>
					</span>
				</div>
			</nav>

			<!-- 右侧操作区 -->
			<div class="header-actions">
				<!-- 订单 -->
				<div class="cart-btn cursor-pointer mr-1" @click="gotoPath('/mall/orders')">
					<div class="bg_gray inline-block icon-swap">
						<el-image style="width: 24px; height: 24px" :src="headerOrder" class="icon-default" />

						<el-image style="width: 24px; height: 24px" :src="hoverheaderOrder" class="icon-hover" />
					</div>
				</div>

				<!-- 购物车 -->
				<span class="cart-btn cursor-pointer mr-4" @click="openCart">
					<el-badge :value="totalQuantity" :hidden="!userInfo.id || !totalQuantity" class="cart-badge">
						<div class="bg_gray icon-swap">
							<el-image style="width: 24px; height: 24px" :src="headerCar" class="icon-default" />
							<el-image style="width: 24px; height: 24px" :src="hoverheaderCar" class="icon-hover" />
						</div>
					</el-badge>
				</span>

				<!-- 多语言切换 -->
				<setlang-select :border="false" width="170px"></setlang-select>

				<el-divider direction="vertical" style="margin: 0" />
				<!-- 用户 -->
				<div class="flex items-center cursor-pointer icon-swap" @click="gotoPath('/mall/profile')">
					<img :src="headUser" class="w-[24px] icon-default" alt="" />

					<img :src="hoverHeadUser" class="w-[24px] icon-hover" alt="" />

					<el-tooltip v-if="userInfo.id" class="box-item" effect="dark" :content="userInfo.nickname" placement="top-start">
						<div class="ml-8px text-[14px] text-[#6D6A75] user-name">{{ userInfo.nickname }}</div>
					</el-tooltip>
					<!-- 未登录 -->
					<span v-else class="ml-1 text-[14px] text-[#6D6A75] user-name">{{ $t('uChoicePc.notLoggedIn') }}</span>
				</div>
			</div>
		</div>

		<!-- 下拉面板（全局定位） -->
		<transition name="dropdown-fade">
			<div v-if="activeDropdown && activeDropdownData" class="dropdown-panel" @mouseenter="onDropdownEnter" @mouseleave="onDropdownLeave">
				<div class="dropdown-inner">
					<div class="dropdown-grid">
						<!-- 二级 -->
						<div v-for="sub in activeDropdownData.child" :key="sub.id" class="mr-80px last:mr-0">
							<div class="mb-20px text-[14px] text-[#9D9AA5] font-medium cursor-pointer hover:text-[#F92863]" @click="secondClick(sub.id)">{{ sub.name?.value }}</div>
							<!-- 三级 -->
							<div v-for="chl in sub.child" :key="chl.id" class="mb-20px last:mb-0 cursor-pointer" @click="thirdClick(sub.id, chl.id)">
								<span class="text-[14px] text-[#333333] hover:text-[#F92863]">{{ chl.name?.value }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</header>

	<cart ref="cartRef"></cart>

	<login-drawer ref="loginDrawerRef"></login-drawer>

	<register-dialog ref="registerDialogRef"></register-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, toRef, type PropType } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowDown } from '@element-plus/icons-vue'

import { getStore } from '@/utils/store'
import cart from '@/views/mall/cart/index.vue'
import registerDialog from '@/views/mall/components/register-dialog/index.vue'
import loginDrawer from '@/views/mall/components/login-drawer/index.vue'
import type { Category } from '@/views/mall/types'
import setlangSelect from './setLangSelect.vue'
import useUserStore from '@/store/user'

const userStore = useUserStore()

const route = useRoute()
const router = useRouter()
const headerLogo = new URL(`@/assets/img/header_logo.png`, import.meta.url).href
const headerOrder = new URL(`@/assets/img/header_order.png`, import.meta.url).href
const hoverheaderOrder = new URL(`@/assets/img/hover_header_order.png`, import.meta.url).href
const headerCar = new URL(`@/assets/img/header_car.png`, import.meta.url).href
const hoverheaderCar = new URL(`@/assets/img/hover_header_car.png`, import.meta.url).href
const headUser = new URL(`@/assets/img/head_user.png`, import.meta.url).href
const hoverHeadUser = new URL(`@/assets/img/hover_head_user.png`, import.meta.url).href
const userInfo = ref({
	nickname: '',
	avatar: '',
	phoneNumber: '',
	id: ''
})
// 导航项
interface NavItem {
	id: string
	name: { value: string }
	path: string
	child?: Category[]
	langKey?: string
}
const loginDrawerRef = ref()
const totalQuantity = ref(0)

const props = defineProps({
	navItems: {
		type: Array as PropType<NavItem[]>,
		default: () => [] as NavItem[]
	}
})
const navItems = toRef(props, 'navItems')

// 监听是否需要展示登录弹窗
watch(
	() => userStore.isShowLogin,
	(newVal) => {
		if (newVal) {
			loginDrawerRef.value?.open()
		}
	},
	{ deep: true, immediate: true }
)

// 监听购物车数量
watch(
	() => userStore.cartQuantity,
	(newVal) => {
		totalQuantity.value = newVal
	},
	{ deep: true, immediate: true }
)

// 监听pinia里的userInfo
watch(
	() => userStore.userInfo,
	(newVal) => {
		userInfo.value = newVal
	},
	{ deep: true, immediate: true }
)

onMounted(() => {
	let loginInfo = getStore({ name: 'loginInfo' })
	if (loginInfo) {
		userInfo.value = loginInfo
		userStore.getCartQuantity()
	}
})

const cartRef = ref()
const openCart = () => {
	if (userInfo.value.id) {
		cartRef.value.open()
	} else {
		userStore.setUpdateLogin()
	}
}

// 下拉状态
const activeDropdown = ref<string | null>(null)
let hideTimer: ReturnType<typeof setTimeout> | null = null

const activeDropdownData = computed(() => {
	if (!activeDropdown.value) return null
	const data = navItems.value.find((item) => item.id === activeDropdown.value) || null
	return data
})

// 判断当前路由是否激活
const isActive = (item: NavItem): boolean => {
	if (item.id) {
		const catId = route.query.id as string
		if (catId) {
			return catId === item.id
		}
		return false
	} else {
		return route.path === '/mall/about'
	}
}

// hover 进入导航项
const onNavEnter = (item: NavItem) => {
	if (hideTimer) {
		clearTimeout(hideTimer)
		hideTimer = null
	}
	if (item.child && item.child.length > 0) {
		activeDropdown.value = item.id
	}
}

// hover 离开导航项
const onNavLeave = (_item: NavItem) => {
	hideTimer = setTimeout(() => {
		activeDropdown.value = null
	}, 150)
}

// hover 进入下拉面板
const onDropdownEnter = () => {
	if (hideTimer) {
		clearTimeout(hideTimer)
		hideTimer = null
	}
}

// hover 离开下拉面板
const onDropdownLeave = () => {
	activeDropdown.value = null
}

const gotoNav = (item: any) => {
	if (item.path === '/mall/category/') {
		router.push({
			path: '/mall/category/' + item.id,
			query: {
				id: item.id,
				name: item.name?.value
			}
		})
	} else {
		router.push({
			path: '/mall/about',
			query: {}
		})
	}
}

const findParentByChildId: any = (tree: any, targetId: number) => {
	for (const node of tree) {
		// 当前节点的子节点包含目标id，当前node就是父
		if (node.child?.some((child: any) => child.id === targetId)) {
			return node
		}
		// 递归遍历子树
		if (node.child?.length) {
			const parent = findParentByChildId(node.child, targetId)
			if (parent) return parent
		}
	}
	return null
}

// 点击第二级
const secondClick = (secondId: number) => {
	let firstNode = findParentByChildId(navItems.value, secondId)
	if (!firstNode) return
	router.push({
		path: '/mall/category/' + firstNode.id,
		query: {
			id: firstNode.id,
			name: firstNode.name?.value,
			secondId
		}
	})
}

// 点击第三级
const thirdClick = (secondId: number, thirdId: number) => {
	let firstNode = findParentByChildId(navItems.value, secondId)
	if (!firstNode) return
	router.push({
		path: '/mall/category/' + firstNode.id,
		query: {
			id: firstNode.id,
			name: firstNode.name?.value,
			secondId,
			thirdId
		}
	})
}

const gotoPath = (path: string) => {
	const isLogo = checkUser()
	if (!isLogo) {
		loginDrawerRef.value.open()
	} else {
		router.push(path)
	}
}

// 检查是否登录
const checkUser = () => {
	const userId = userInfo.value.id
	if (!userId) {
		loginDrawerRef.value.open()
		return false
	}
	return true
}
</script>

<style lang="scss" scoped>
.mall-header {
	position: sticky;
	top: 0;
	z-index: 100;
	background: #fff;
	border-bottom: 1px solid #f0f0f0;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

	&.has-dropdown {
		box-shadow: none;
	}
}

.bg_gray {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #f9f8f8;

	&:hover {
		background: #f5f6f8;
	}
}

/* hover 切换图标 */
.icon-swap {
	.icon-hover {
		display: none;
	}

	&:hover {
		.icon-default {
			display: none;
		}

		.icon-hover {
			display: inline-block;
		}
	}
}

.header-inner {
	margin: 0 auto;
	height: 108px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 12.8vw;
}

/* ========== Logo ========== */
.logo {
	display: flex;
	align-items: center;
	gap: 8px;
	text-decoration: none;
	flex-shrink: 0;

	.logo-icon {
		font-size: 28px;
		line-height: 1;
	}

	.logo-text {
		font-size: 20px;
		font-weight: 700;
		color: #f92863;
		letter-spacing: 0.5px;
	}
}

/* ========== 导航菜单 ========== */
.header-nav {
	max-width: 40vw;
	display: flex;
	align-items: center;
	margin: 0 60px 0 24px;
	height: 100%;
}

.nav-item-wrapper {
	height: 100%;
	display: flex;
	align-items: center;
	margin: 0 8px;
}

.nav-item {
	display: flex;
	align-items: center;
	margin: 0 4px;
	padding: 0 8px;
	height: 28px;
	font-size: 16px;
	color: #0d0421;
	border-radius: 8px;
	line-height: 24px;
	transition:
		color 0.2s,
		background 0.2s;
	position: relative;
	cursor: pointer;
	box-sizing: border-box;

	&:hover {
		background: #f9f8f8;
		color: #f92863;
	}

	&.nav-active {
		background-color: #f92863;
		color: #fff;
		font-weight: 600;
	}

	.nav-arrow {
		font-size: 12px;
		transition: transform 0.2s;
	}
}

/* ========== 右侧操作区 ========== */
.header-actions {
	// margin-left: auto;

	display: flex;
	align-items: center;
	gap: 16px;
	flex-shrink: 0;
}

.search-box {
	width: 220px;

	.search-input {
		:deep(.el-input__wrapper) {
			border-radius: 20px;
			background: #f5f6f8;
			border: 1px solid transparent;
			box-shadow: none;
			transition: all 0.2s;
			padding: 0 12px;

			.el-input__inner {
				font-size: 13px;
				color: #333;

				&::placeholder {
					color: #bbb;
				}
			}

			.el-input__prefix {
				color: #999;
			}
		}

		:deep(.el-input__suffix) {
			display: none;
		}
	}

	&.is-focused .search-input {
		:deep(.el-input__wrapper) {
			background: #fff;
			border-color: #f92863;
		}
	}
}

.search-icon {
	font-size: 16px;
}

.cart-badge {
	:deep(.el-badge__content) {
		background-color: #f92863;
		border: 2px solid #fff;
	}
}

/* ========== 下拉面板 ========== */
.dropdown-panel {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: #fff;
	border-top: 1px solid #f0f0f0;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
	z-index: 99;
}

.dropdown-inner {
	margin: 0 auto;
	padding: 28px 12.8vw 32px;
}

.dropdown-grid {
	display: flex;
	// justify-content: center;
}

.sub-category-card {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	text-decoration: none;
	border-radius: 10px;
	transition: all 0.2s;

	&:hover {
		background: #fafafa;

		.sub-category-name {
			color: #f92863;
		}
	}
}

.sub-category-img {
	width: 56px;
	height: 56px;
	border-radius: 8px;
	overflow: hidden;
	background: #f5f6f8;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.sub-category-placeholder {
		font-size: 24px;
		line-height: 1;
	}
}

.sub-category-name {
	font-size: 14px;
	color: #333;
	transition: color 0.2s;
}

/* ========== 下拉动画 ========== */
.dropdown-fade-enter-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}

.dropdown-fade-leave-active {
	transition:
		opacity 0.15s ease,
		transform 0.15s ease;
}

.dropdown-fade-enter-from {
	opacity: 0;
}

.dropdown-fade-leave-to {
	opacity: 0;
}
.user-name {
	width: 80px;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
  &:hover{
    color: #f92863;
  }
}
.nav-name {
	max-width: 80px;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
:deep(.el-badge__content.is-fixed) {
  top: 4px;
}
</style>
